// src/services/patientPresenceService.js
//
// Lightweight "who else has this patient open" presence tracking.
// Each active viewer writes a heartbeat doc to
// patient_presence/{patientId}/viewers/{uid}; viewers whose heartbeat is
// older than PRESENCE_STALE_MS are treated as no longer present. Identity
// is never surfaced to other viewers — callers only get back a boolean.

import { db } from "../firebase";
import {
  collection,
  deleteDoc,
  doc,
  onSnapshot,
  serverTimestamp,
  setDoc,
} from "firebase/firestore";
import { getPatientPresenceViewersPath } from "./firestorePaths";

export const PRESENCE_HEARTBEAT_MS = 20000;
export const PRESENCE_STALE_MS = 60000;

export const markPatientPresence = async (patientId, uid) => {
  if (!patientId || !uid) return;
  try {
    await setDoc(
      doc(db, getPatientPresenceViewersPath(patientId), uid),
      { uid, updatedAt: serverTimestamp() },
      { merge: true }
    );
  } catch (err) {
    console.error("Failed to update patient presence:", err);
  }
};

export const clearPatientPresence = async (patientId, uid) => {
  if (!patientId || !uid) return;
  try {
    await deleteDoc(doc(db, getPatientPresenceViewersPath(patientId), uid));
  } catch (err) {
    console.error("Failed to clear patient presence:", err);
  }
};

// Subscribes to other active viewers of this patient. Calls back with a
// boolean: true if at least one OTHER user has a recent heartbeat.
export const subscribeToOtherViewers = (patientId, uid, callback) => {
  if (!patientId || !uid) return () => {};

  return onSnapshot(
    collection(db, getPatientPresenceViewersPath(patientId)),
    (snap) => {
      const now = Date.now();
      const hasOtherActiveViewer = snap.docs.some((d) => {
        if (d.id === uid) return false;
        const updatedAt = d.data()?.updatedAt;
        const updatedAtMs =
          typeof updatedAt?.toMillis === "function"
            ? updatedAt.toMillis()
            : null;
        if (!updatedAtMs) return false;
        return now - updatedAtMs < PRESENCE_STALE_MS;
      });
      callback(hasOtherActiveViewer);
    },
    (err) => {
      console.error("Patient presence subscribe error:", err);
    }
  );
};
